/**
 * Dispute detail — read-only timeline of a rental_dispute.
 *
 * Renders: header pill, claim section (description + photos + amount),
 * response section (or "awaiting response" state with deadline
 * countdown), resolution section (if resolved). Subscribes to
 * realtime UPDATEs via `useDispute` so the status pill flips when the
 * escalation cron or admin resolution lands.
 *
 * Conditional CTA: if the viewer is the respondent AND status is
 * awaiting_response/open, surfaces a "Responder" button that routes
 * to /booking/dispute/{id}/respond.
 */
import { useMemo } from 'react'
import { View, Text, ScrollView } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Image as ExpoImage } from 'expo-image'
import { toPlaceholder, GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'
import {
  Camera,
  Image as ImageIcon,
  Calendar,
  AlertCircle,
  CheckCircle2,
} from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { ScreenState } from '@/components/ui/ScreenState'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { DisputeStatusPill } from '@/components/disputes/DisputeStatusPill'
import { useDispute } from '@/lib/hooks/use-disputes'
import { useAuth } from '@/lib/hooks/use-auth'
import { useSemanticColors, SPACING, RADIUS, withAlpha } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import type { EvidenceMetadata } from '@/lib/types/dispute.types'
import { formatCurrency } from '@/lib/utils/format'

interface CountdownTone {
  tone: 'success' | 'amber' | 'coral' | 'neutral'
  label: string
}

function deadlineCountdown(
  deadline: string | null,
  expiredLabel: string,
  prefix: string,
): CountdownTone {
  if (!deadline) return { tone: 'neutral', label: '' }
  const ms = new Date(deadline).getTime() - Date.now()
  if (ms <= 0) return { tone: 'coral', label: expiredLabel }
  const hours = Math.floor(ms / (60 * 60 * 1000))
  const tone = hours >= 24 ? 'success' : hours >= 6 ? 'amber' : 'coral'
  return { tone, label: `${prefix} ${hours}h` }
}

export default function DisputeDetailScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const params = useLocalSearchParams<{ disputeId: string }>()
  const { user } = useAuth()
  const userId = user?.id ?? ''

  // The whole query object, not just `data` — ScreenState needs isError +
  // refetch so a failed load offers a retry instead of a forever-skeleton.
  const disputeQuery = useDispute(params.disputeId)
  const dispute = disputeQuery.data

  const viewerRole = useMemo<'claimant' | 'respondent' | 'observer'>(() => {
    if (!dispute) return 'observer'
    if (dispute.claimantId === userId) return 'claimant'
    if (dispute.respondentId === userId) return 'respondent'
    return 'observer'
  }, [dispute, userId])

  const canRespond =
    viewerRole === 'respondent' &&
    dispute &&
    (dispute.status === 'awaiting_response' || dispute.status === 'open') &&
    !dispute.respondedAt

  const countdown = useMemo(() => {
    if (!dispute || dispute.status !== 'awaiting_response') return null
    return deadlineCountdown(
      dispute.responseDeadline,
      t('disputes.deadline_expired'),
      t('disputes.deadline_remaining'),
    )
  }, [dispute, t])

  // One resolver for the countdown tone, matching DisputeStatusPill: the colour
  // is decorative, the text is textPrimary. amber/green as TEXT on a wash of
  // themselves measured under AA at 13px.
  const countdownTone =
    countdown?.tone === 'coral'
      ? COLORS.coral.DEFAULT
      : countdown?.tone === 'amber'
        ? isDark
          ? COLORS.amber.DEFAULT
          : COLORS.amber.dark
        : colors.success

  return (
    <ScreenContainer scroll={false}>
      {/* Header stays mounted across loading → error → content, so the swap
          doesn't remount the container or lose the back button. */}
      <ScreenHeader title={t('disputes.detail_title')} />

      <ScreenState
        query={disputeQuery}
        skeleton={
          <View style={{ paddingHorizontal: SPACING.lg, gap: SPACING.md }}>
            <Skeleton width={140} height={20} borderRadius={RADIUS.full} />
            <Skeleton width="100%" height={120} borderRadius={RADIUS.lg} />
            <Skeleton width="100%" height={200} borderRadius={RADIUS.lg} />
          </View>
        }
      >
        {(dispute) => (
          <ScrollView
            contentContainerStyle={{
              paddingHorizontal: SPACING.lg,
              paddingBottom: SPACING.xl * 2,
              gap: SPACING.lg,
            }}
          >
            {/* Status + category */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm,
                flexWrap: 'wrap',
              }}
            >
              <DisputeStatusPill status={dispute.status} />
              <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
                {t(`disputes.category_${dispute.category}`)}
              </Text>
            </View>

            {/* Deadline countdown banner — only when awaiting response */}
            {countdown && countdown.label ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.sm,
                  padding: SPACING.md,
                  borderRadius: RADIUS.lg,
                  // Same strategy as DisputeStatusPill: the tone is decorative
                  // (wash + icon) and the sentence itself is textPrimary. The
                  // amber and green used to be the TEXT colour on a 14% wash of
                  // themselves, which measures under AA at 13px — and the pill
                  // two rows away had already been moved off that pattern.
                  backgroundColor: withAlpha(countdownTone, isDark ? 0.22 : 0.16),
                  borderWidth: 1,
                  borderColor: withAlpha(countdownTone, isDark ? 0.45 : 0.35),
                }}
              >
                <Calendar size={18} color={countdownTone} />
                <Text
                  style={{
                    fontSize: 13,
                    fontFamily: FONTS.bodyBold,
                    color: countdownTone,
                  }}
                >
                  {countdown.label}
                </Text>
              </View>
            ) : null}

            {/* Claim section */}
            <Section title={t('disputes.claim_section')} colors={colors}>
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.body,
                  color: colors.textPrimary,
                  lineHeight: 20,
                }}
              >
                {dispute.claimDescription}
              </Text>
              {dispute.claimAmountCents > 0 && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
                  <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
                    {t('disputes.amount_claimed')}
                  </Text>
                  <Text
                    style={{ fontSize: 14, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}
                  >
                    {formatCurrency(dispute.claimAmountCents)}
                  </Text>
                </View>
              )}
              <PhotoGrid
                urls={dispute.claimEvidenceUrls}
                metadata={dispute.claimEvidenceMetadata}
                colors={colors}
              />
            </Section>

            {/* Response section */}
            {dispute.respondedAt ? (
              <Section title={t('disputes.response_section')} colors={colors}>
                {dispute.responseDescription ? (
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: FONTS.body,
                      color: colors.textPrimary,
                      lineHeight: 20,
                    }}
                  >
                    {dispute.responseDescription}
                  </Text>
                ) : null}
                <PhotoGrid
                  urls={dispute.responseEvidenceUrls}
                  metadata={dispute.responseEvidenceMetadata}
                  colors={colors}
                />
              </Section>
            ) : dispute.status === 'awaiting_response' ? (
              <Section title={t('disputes.response_section')} colors={colors}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
                  <AlertCircle size={16} color={colors.textMuted} />
                  <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textMuted }}>
                    {t('disputes.no_response_yet')}
                  </Text>
                </View>
              </Section>
            ) : null}

            {/* Resolution section */}
            {dispute.resolvedAt ? (
              <Section title={t('disputes.resolution_section')} colors={colors}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
                  <CheckCircle2 size={18} color={colors.success} />
                  <Text
                    style={{ fontSize: 14, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}
                  >
                    {t(`disputes.status_${dispute.status}`)}
                  </Text>
                </View>
                {dispute.resolutionNotes ? (
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                      lineHeight: 20,
                    }}
                  >
                    {dispute.resolutionNotes}
                  </Text>
                ) : null}
                {dispute.resolvedAmountCents != null && dispute.resolvedAmountCents > 0 && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
                    <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
                      {t('disputes.amount_resolved')}
                    </Text>
                    <Text
                      style={{
                        fontSize: 14,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {formatCurrency(dispute.resolvedAmountCents)}
                    </Text>
                  </View>
                )}
              </Section>
            ) : null}

            {canRespond && (
              <Button
                title={t('disputes.respond_cta')}
                onPress={() => router.push(`/booking/dispute/${dispute.id}/respond` as never)}
                variant="primary"
                size="lg"
                testID="disputes.respond-cta"
              />
            )}
          </ScrollView>
        )}
      </ScreenState>
    </ScreenContainer>
  )
}

function Section({
  title,
  colors,
  children,
}: {
  title: string
  colors: ReturnType<typeof useSemanticColors>
  children: React.ReactNode
}) {
  return (
    <View
      style={{
        gap: SPACING.sm,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        backgroundColor: colors.surface,
        ...WEB_SHADOWS.sm,
      }}
    >
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.bodyBold,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        {title}
      </Text>
      {children}
    </View>
  )
}

function PhotoGrid({
  urls,
  metadata,
  colors,
}: {
  urls: string[]
  metadata: EvidenceMetadata[]
  colors: ReturnType<typeof useSemanticColors>
}) {
  if (urls.length === 0) return null
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
      {urls.map((url, i) => {
        const meta = metadata[i]
        const Icon = meta?.captureSource === 'camera' ? Camera : ImageIcon
        return (
          <View
            key={`${url}-${i}`}
            style={{
              width: 100,
              height: 100,
              borderRadius: RADIUS.md,
              overflow: 'hidden',
              position: 'relative',
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <ExpoImage
              source={{ uri: url }}
              placeholder={toPlaceholder(GENERIC_IMAGE_BLURHASH)}
              style={{ width: '100%', height: '100%' }}
              contentFit="cover"
            />
            {meta && (
              <View
                style={{
                  position: 'absolute',
                  bottom: 4,
                  left: 4,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  paddingHorizontal: 5,
                  paddingVertical: 2,
                  borderRadius: RADIUS.xs,
                  backgroundColor: 'rgba(0,0,0,0.55)',
                }}
              >
                <Icon size={10} color="#FFFFFF" strokeWidth={2} />
              </View>
            )}
          </View>
        )
      })}
    </View>
  )
}
